/**
 * In-memory capture for the feedback widget (#120). Nothing here is persisted or sent anywhere until the user files a
 * report: the SPA keeps the last few console messages and API request entries so a bug report can say what was going
 * on, and redacts them BEFORE they are stored (secrets never sit in the buffer, let alone leave the browser).
 */

export const ACTIVITY_MAX_ENTRIES = 200;
export const CONTEXT_CONSOLE_MAX_ENTRIES = 20;
export const MAX_MESSAGE_LENGTH = 500;

const AUTH_HEADER_RE = /\b(Bearer|Basic)\s+[A-Za-z0-9\-._~+/]+=*/gi;
const JSON_SECRET_FIELD_RE =
  /("(?:token|access_token|id_token|refresh_token|api_key|apikey|password|secret)"\s*:\s*")[^"]*(")/gi;
const SENSITIVE_QUERY_KEY_RE =
  /\b(token|access_token|id_token|refresh_token|code|state|api_key|apikey|key|password|secret|sig|signature)=[^&\s"'<>]+/gi;
// Bounded repetition (RFC 5321 limits): an unbounded form backtracks quadratically on adversarial input.
const EMAIL_RE = /[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24}/g;

/** Strip credential and personal-data shapes from one line of free text. */
export function redactText(text: string): string {
  return text
    .replace(AUTH_HEADER_RE, '$1 [REDACTED]')
    .replace(JSON_SECRET_FIELD_RE, '$1[REDACTED]$2')
    .replace(SENSITIVE_QUERY_KEY_RE, (m) => `${m.slice(0, m.indexOf('=') + 1)}[REDACTED]`)
    .replace(EMAIL_RE, '[REDACTED-EMAIL]');
}

/** One line from console arguments, like the browser joins them: no stack, no second line, redacted, then capped. */
export function formatConsoleArgs(args: unknown[]): string {
  const parts = args.map((a) => {
    if (a instanceof Error) return a.message || a.name || String(a);
    if (typeof a === 'string') return a;
    try {
      return JSON.stringify(a) ?? String(a);
    } catch {
      return String(a);
    }
  });
  const firstLine = redactText(parts.join(' ')).split('\n')[0];
  return firstLine.length > MAX_MESSAGE_LENGTH ? firstLine.slice(0, MAX_MESSAGE_LENGTH) : firstLine;
}

export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error';

export type ActivityEntry =
  | { kind: 'console'; level: ConsoleLevel; message: string; ts: string }
  | { kind: 'network'; method: string; path: string; status: number; durationMs: number; ts: string };

let activity: ActivityEntry[] = [];
let installed = false;

function push(entry: ActivityEntry): void {
  activity.push(entry);
  if (activity.length > ACTIVITY_MAX_ENTRIES) activity = activity.slice(activity.length - ACTIVITY_MAX_ENTRIES);
}

/** A request path for the log: no host, query credentials redacted. */
export function redactRequestPath(rawPath: string): string {
  const noFragment = rawPath.split('#')[0];
  return redactText(noFragment).slice(0, MAX_MESSAGE_LENGTH);
}

/** Record one finished API request (method, path, status, duration): never a body, never a header. */
export function recordApiRequest(r: { method: string; path: string; status: number; durationMs: number }): void {
  push({
    kind: 'network',
    method: r.method.toUpperCase(),
    path: redactRequestPath(r.path),
    status: r.status,
    durationMs: Math.max(0, Math.round(r.durationMs)),
    ts: new Date().toISOString(),
  });
}

/**
 * Tee console.log/info/warn/error into the buffer, once per page load. The original method is always called, so the
 * developer console is unaffected.
 */
export function installFeedbackCapture(): void {
  if (installed) return;
  installed = true;
  for (const level of ['log', 'info', 'warn', 'error'] as const) {
    // eslint-disable-next-line no-console
    const original = console[level].bind(console);
    // eslint-disable-next-line no-console
    console[level] = (...args: unknown[]) => {
      push({ kind: 'console', level, message: formatConsoleArgs(args), ts: new Date().toISOString() });
      original(...args);
    };
  }
}

/** Everything captured, oldest first (copies, so a caller cannot change the live buffer). */
export function getActivity(): ActivityEntry[] {
  return activity.map((e) => ({ ...e }));
}

/** The last 20 console warnings/errors, in the shape QA's intake takes as `context.consoleLog`. */
export function getContextConsoleLog(): { level: 'warn' | 'error'; message: string; ts: string }[] {
  const out: { level: 'warn' | 'error'; message: string; ts: string }[] = [];
  for (const e of activity) {
    if (e.kind === 'console' && (e.level === 'warn' || e.level === 'error')) {
      out.push({ level: e.level, message: e.message, ts: e.ts });
    }
  }
  return out.slice(-CONTEXT_CONSOLE_MAX_ENTRIES);
}

/** The captured activity as the two text blocks the intake takes (`consoleLogs`, `networkLogs`). */
export function formatActivity(): { consoleLogs: string; networkLogs: string } {
  const consoleLines: string[] = [];
  const networkLines: string[] = [];
  for (const e of activity) {
    if (e.kind === 'console') consoleLines.push(`[${e.ts}] ${e.level.toUpperCase()}: ${e.message}`);
    else networkLines.push(`[${e.ts}] ${e.method} ${e.path} → ${e.status} ${e.durationMs}ms`);
  }
  return { consoleLogs: consoleLines.join('\n'), networkLogs: networkLines.join('\n') };
}

/** Test-only: forget everything and allow a fresh install against a re-mocked console. */
export function resetFeedbackCaptureForTests(): void {
  activity = [];
  installed = false;
}
